const DEFAULT_BREADCRUMB_TTL_MS = 3600000

const isNonArrayObject = value =>
  value && typeof value === 'object' && !Array.isArray(value)

const parseJSONIfPossible = payload => {
  if (typeof payload !== 'string') return payload

  const trimmed = payload.trim()
  const looksLikeJSON =
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))

  if (!looksLikeJSON) return payload

  try {
    return JSON.parse(trimmed)
  } catch {
    return payload
  }
}

const serializePayloadForConsumers = payload =>
  typeof payload === 'string' ? payload : JSON.stringify(payload)

const isEnvelopeExpired = (timestamp, maxAgeMs) =>
  !Number.isFinite(timestamp) || Date.now() - timestamp > maxAgeMs

const extractPayloadFromEnvelope = (parsed, maxAgeMs) => {
  if (!isNonArrayObject(parsed)) return null

  const timestamp = Number(parsed.timestamp)
  const { payload } = parsed

  if (payload == null || payload === '') return null
  if (isEnvelopeExpired(timestamp, maxAgeMs)) return null

  return serializePayloadForConsumers(payload)
}

const withSessionStorage = (onSuccess, fallbackValue) => {
  try {
    if (typeof sessionStorage === 'undefined') return fallbackValue
    return onSuccess(sessionStorage)
  } catch {
    return fallbackValue
  }
}

const runSessionStorageOperation = (key, fallbackValue, operation) => {
  if (!key) return fallbackValue
  return withSessionStorage(storage => operation(storage), fallbackValue)
}

const readSessionItem = key =>
  runSessionStorageOperation(key, null, storage => storage.getItem(key))

const writeSessionItem = (key, value) =>
  runSessionStorageOperation(key, false, storage => {
    storage.setItem(key, value)
    return true
  })

const removeSessionItem = key =>
  runSessionStorageOperation(key, false, storage => {
    storage.removeItem(key)
    return true
  })

const shouldCleanupStoredRecord = stored =>
  !stored || typeof stored !== 'string'

const hasOwnRegionField = parsed =>
  isNonArrayObject(parsed) && Object.hasOwn(parsed, 'region')

export const writeBreadcrumbSessionRecord = (
  key,
  payload = '',
  region = undefined
) => {
  if (!key || !payload) return false

  const normalizedPayload = parseJSONIfPossible(payload)

  return writeSessionItem(
    key,
    JSON.stringify({
      timestamp: Date.now(),
      payload: normalizedPayload,
      region,
    })
  )
}

export const readBreadcrumbSessionRecord = (
  key,
  maxAgeMs = DEFAULT_BREADCRUMB_TTL_MS,
  currentRegion = undefined
) => {
  const stored = readSessionItem(key)
  if (shouldCleanupStoredRecord(stored)) {
    removeSessionItem(key)
    return null
  }

  const parsed = parseJSONIfPossible(stored)
  if (!isNonArrayObject(parsed)) {
    removeSessionItem(key)
    return null
  }

  if (currentRegion && isNonArrayObject(parsed) && !hasOwnRegionField(parsed)) {
    removeSessionItem(key)
    return null
  }

  if (
    currentRegion &&
    isNonArrayObject(parsed) &&
    parsed.region !== currentRegion
  ) {
    removeSessionItem(key)
    return null
  }

  const payload = extractPayloadFromEnvelope(parsed, maxAgeMs)
  if (payload !== null) return payload

  removeSessionItem(key)
  return null
}
